/**
 * Cross-feature trigger surface for the Income/Expense (Кирим/Чиқим)
 * transaction modal.
 *
 * Architecture (Modal Manager pattern):
 *   - The MODAL component (`TxnFormModal`) lives in `features/accounting/`
 *     where its domain logic belongs — Transaction types, mutation hooks,
 *     category taxonomy, etc.
 *   - This STORE lives in `shared/` and carries only the open/close state
 *     plus a thin opaque payload. Any feature can dispatch to it.
 *   - The `TransactionModalRoot` widget (in `widgets/transaction-modal-root/`)
 *     subscribes to this store, renders `<TxnFormModal>` once globally, and
 *     intercepts the save-success callback to invalidate caller-specified
 *     queries before closing.
 *
 * Why a shared store (and not direct import of TxnFormModal):
 *   - FSD forbids `features → features` imports. A button in Supply or
 *     Finance cannot import the accounting feature's modal directly.
 *   - Dispatching to a shared store is the FSD-compliant alternative
 *     (`shared` is allowed from any layer).
 *
 * Re-fetch contract (two layers):
 *   1. CANONICAL — accounting's own transactions/invoices/dashboard caches.
 *      Handled INSIDE `useCreateTransaction` / `useUpdateTransaction` by
 *      `useEntityInvalidate(ACCOUNTING_PREFIX)`. Fires regardless of who
 *      opened the modal. Callers don't have to opt in.
 *   2. CALLER-SPECIFIED — derived queries the calling page owns. Passed via
 *      `invalidateOnSuccess` per-open. The widget invalidates them after the
 *      mutation succeeds, before invoking the per-open `onSuccess` callback.
 *
 * @example Finance dashboard adds a quick "+Кирим" button
 * ```tsx
 * import { openTransactionCreate } from '@/shared/stores'
 *
 * <Button onClick={() => openTransactionCreate('INCOME', {
 *   invalidateOnSuccess: ['/v1/finance/balance', '/v1/finance/dashboard'],
 *   onSuccess: () => trackEvent('finance.income.added'),
 * })}>
 *   +Кирим
 * </Button>
 * ```
 *
 * @example Supply page edits a transaction inline (Transaction object in hand)
 * ```tsx
 * import { openTransactionEdit } from '@/shared/stores'
 *
 * <Button onClick={() => openTransactionEdit(txn, {
 *   invalidateOnSuccess: ['/v1/supply/pending'],
 * })}>Tahrirlash</Button>
 * ```
 *
 * The `mode` payload field is typed as `unknown` to avoid a
 * `shared → features` import for `TxnFormMode`. The dispatching helpers
 * (`openTransactionCreate`, `openTransactionEdit`) give callers a type-safe
 * surface without leaking the accounting-feature types into this module.
 * The modal-root widget casts back to `TxnFormMode` at the boundary.
 */

import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

interface TransactionModalPayload {
  /**
   * Structurally a `TxnFormMode` (`{ kind: 'create'; type } | { kind: 'edit'; txn }`).
   * Kept opaque here to respect the `shared → features` FSD constraint; the
   * `TransactionModalRoot` widget casts at the render boundary.
   */
  mode: unknown
  /** Query-key prefixes to invalidate via `queryClient.invalidateQueries` after a successful save. */
  invalidateOnSuccess?: ReadonlyArray<string | readonly string[]>
  /** Called after canonical + caller-specified invalidations run, before the modal closes. */
  onSuccess?: () => void
}

interface TransactionModalStore {
  isOpen: boolean
  payload: TransactionModalPayload | null
  open: (payload: TransactionModalPayload) => void
  close: () => void
}

export const useTransactionModalStore = create<TransactionModalStore>((set) => ({
  isOpen: false,
  payload: null,
  open: (payload) => set({ isOpen: true, payload }),
  close: () => set({ isOpen: false, payload: null }),
}))

/**
 * Selector hook for components that need to react to the modal state
 * (typically only `TransactionModalRoot`). Returns shallow-equal slice;
 * all values + actions are stable across renders.
 */
export function useTransactionModal() {
  return useTransactionModalStore(
    useShallow((s) => ({
      isOpen: s.isOpen,
      payload: s.payload,
      open: s.open,
      close: s.close,
    })),
  )
}

interface OpenOpts {
  invalidateOnSuccess?: ReadonlyArray<string | readonly string[]>
  onSuccess?: () => void
}

/**
 * Open the transaction modal in CREATE mode. Usable from any feature.
 *
 * @param type   'INCOME' for Кирим, 'EXPENSE' for Чиқим.
 * @param opts   Per-open invalidation prefixes + success callback.
 */
export function openTransactionCreate(type: 'INCOME' | 'EXPENSE', opts?: OpenOpts) {
  useTransactionModalStore.getState().open({
    mode: { kind: 'create', type },
    invalidateOnSuccess: opts?.invalidateOnSuccess,
    onSuccess: opts?.onSuccess,
  })
}

/**
 * Open the transaction modal in EDIT mode pre-filled from a `Transaction`.
 *
 * @param txn   A `Transaction` from `@/features/accounting/api/contracts`.
 *              Typed `unknown` here to keep `shared` free of feature imports;
 *              the modal-root widget casts back when rendering.
 * @param opts  Per-open invalidation prefixes + success callback.
 */
export function openTransactionEdit(txn: unknown, opts?: OpenOpts) {
  useTransactionModalStore.getState().open({
    mode: { kind: 'edit', txn },
    invalidateOnSuccess: opts?.invalidateOnSuccess,
    onSuccess: opts?.onSuccess,
  })
}

/** Imperative close — exposed for non-React callers (cancellation flows, tests). */
export function closeTransactionModal() {
  useTransactionModalStore.getState().close()
}
